import React, { createContext, useContext, useState, useCallback } from 'react';
import { useCatalogContext } from '@magento/peregrine/lib/context/catalog';
import { useCartContext } from '@overrides_venia/peregrine/context/cart';
import { useCheckoutContext } from '@magento/peregrine/lib/context/checkout';
const MenuTypeConflictContext = createContext();
const SPECIAL_MENU_TYPE_BY_SKU = {
  '003002': 'D',
  '003003': 'D'
};
export const MenuTypeConflictProvider = _ref => {
  let {
    children
  } = _ref;
  const [pendingAddToCart, setPendingAddToCart] = useState(null);
  const [conflictModalOpen, setConflictModalOpenInternal] = useState(false);
  const [{
    products
  }] = useCatalogContext();
  const [{
    details
  }] = useCartContext();
  const [{
    tableData
  }] = useCheckoutContext();

  // Wrapper to log state changes
  const setConflictModalOpen = useCallback(value => {
    console.log('[MenuTypeConflict] setConflictModalOpen called', {
      from: conflictModalOpen,
      to: value,
      pendingProduct: (pendingAddToCart === null || pendingAddToCart === void 0 ? void 0 : pendingAddToCart.productSku) || null
    });
    setConflictModalOpenInternal(value);
  }, [conflictModalOpen, pendingAddToCart]);

  // Check if adding a product would create a menu type conflict
  const wouldCreateMenuTypeConflict = useCallback(newProductSku => {
    var _details$items;
    // Only check for conflicts if sales type is CASUALDINING
    if ((tableData === null || tableData === void 0 ? void 0 : tableData.sales_type) !== "CASUALDINING") return false;
    const newProduct = products.find(p => p.sku === newProductSku);
    const newMenuType = SPECIAL_MENU_TYPE_BY_SKU[newProductSku] || (newProduct === null || newProduct === void 0 ? void 0 : newProduct.menu_type);
    if (!newProduct || !newMenuType) return false;
    const cartProductSKUs = (details === null || details === void 0 ? void 0 : (_details$items = details.items) === null || _details$items === void 0 ? void 0 : _details$items.map(item => item.product.sku)) || [];
    const cartProducts = products.filter(product => cartProductSKUs.includes(product.sku));
    const currentMenuTypes = (cartProducts === null || cartProducts === void 0 ? void 0 : cartProducts.map(p => SPECIAL_MENU_TYPE_BY_SKU[p.sku] || p.menu_type)) || [];

    // Check if adding this product would create a conflict
    const allMenuTypes = [...currentMenuTypes, newMenuType];
    return (allMenuTypes.includes("1") || allMenuTypes.includes("2")) && (allMenuTypes.includes("D") || allMenuTypes.includes("C"));
  }, [products, details, tableData]);

  // Intercept add to cart and check for conflicts
  const interceptAddToCart = useCallback((originalAddToCart, productSku) => {
    if (wouldCreateMenuTypeConflict(productSku)) {
      setPendingAddToCart({
        addToCartFunction: originalAddToCart,
        productSku: productSku
      });
      setConflictModalOpen(true);
      return Promise.resolve(); // Return resolved promise to prevent original function from running
    } else {
      return originalAddToCart();
    }
  }, [wouldCreateMenuTypeConflict]);

  // Confirm adding the conflicting product
  const confirmAddConflictingProduct = useCallback(async () => {
    console.log('[MenuTypeConflict] confirmAddConflictingProduct called', {
      pendingAddToCart
    });
    if (pendingAddToCart) {
      await pendingAddToCart.addToCartFunction();
      setPendingAddToCart(null);
    }
    setConflictModalOpen(false);
  }, [pendingAddToCart, setConflictModalOpen]);

  // Cancel adding the conflicting product
  const cancelAddConflictingProduct = useCallback(() => {
    setPendingAddToCart(null);
    setConflictModalOpen(false);
  }, []);

  // Reset menu type conflict state (called after order is placed)
  const resetMenuTypeConflictState = useCallback(() => {
    setPendingAddToCart(null);
    setConflictModalOpen(false);
  }, []);
  const contextValue = {
    interceptAddToCart,
    confirmAddConflictingProduct,
    cancelAddConflictingProduct,
    resetMenuTypeConflictState,
    pendingProduct: (pendingAddToCart === null || pendingAddToCart === void 0 ? void 0 : pendingAddToCart.productSku) || null,
    conflictModalOpen,
    setConflictModalOpen
  };
  return /*#__PURE__*/React.createElement(MenuTypeConflictContext.Provider, {
    value: contextValue
  }, children);
};
export const useMenuTypeConflict = () => {
  const context = useContext(MenuTypeConflictContext);
  if (!context) {
    throw new Error('useMenuTypeConflict must be used within a MenuTypeConflictProvider');
  }
  return context;
};